import { createUseStyles } from 'react-jss';
import { colors } from '@insurancemenu/imenu-components-lib';
import { ComponentStylesProps } from '@insurancemenu/imenu-components-lib/dist/types';
import { boxShadows } from '@insurancemenu/imenu-components-lib/dist/utils';

export interface PopularPlanCardThemeProps {
  accentColor: string;
  portfoliosQuantity: number;
  highestAdvantagesRowHeight: number;
  icon: React.ReactNode;
}

const styles: ComponentStylesProps<PopularPlanCardThemeProps> = {
  planCard: ({ portfoliosQuantity, highestAdvantagesRowHeight }) => ({
    display: 'grid',
    gridTemplateRows:
      portfoliosQuantity === 1
        ? '6.8rem auto auto 4.4rem 5rem 4.4rem 1fr 4.4rem 1fr 11rem'
        : `6.8rem auto auto 4.4rem 5rem 4.4rem ${
            highestAdvantagesRowHeight ? `${highestAdvantagesRowHeight}px` : '1fr'
          } 4.4rem 2fr 11rem`,
    alignItems: 'start',
    gap: '2rem',
    width: '100%',
    background: colors.neutral.white,
    border: `1px solid ${colors.neutral[300]}`,
    boxShadow: boxShadows.dark[16],
    borderRadius: '1rem',
    paddingBottom: '4rem',
    position: 'relative',
  }),
  '@media(max-width: 834px)': {
    planCard: () => ({
      gridTemplateRows: '6.8rem auto auto 4.4rem 5rem 4.4rem auto 4.4rem auto 11rem',
    }),
  },

  planTag: ({ accentColor }) => ({
    background: accentColor,
    color: colors.neutral.white,
    borderRadius: '0.9rem 0.9rem 0 0',
    textAlign: 'center',
    margin: 0,
    fontWeight: 700,
    fontSize: '2.4rem',
    lineHeight: '3.2rem',
    letterSpacing: '0.02em',
    padding: '1.8rem 0',
    boxSizing: 'border-box',
    minHeight: '6.8rem',
  }),
  planTitle: {
    fontSize: '2rem',
    lineHeight: '2.8rem',
    padding: '0 2rem',
    margin: 0,
    minHeight: '5.6rem',
  },
  section: {
    backgroundColor: colors.neutral[100],
    padding: '1rem 0',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.8rem',
    lineHeight: '2.4rem',
    letterSpacing: '0.03em',
    color: colors.neutral[900],
    textAlign: 'center',
    margin: 0,
  },
  advantagesList: {
    padding: '0 2rem',
    listStyle: 'none',
    margin: 0,
  },
  advantagesItem: {
    fontSize: '1.6rem',
    lineHeight: '2.4rem',
    display: 'grid',
    gap: '1.5rem',
    gridTemplateColumns: '2.4rem auto',

    '&:not(:last-child)': {
      marginBottom: '1rem',
    },
  },
  summaryList: {
    margin: 0,
    padding: '0 2rem',
    listStyle: 'none',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    '&:not(:last-child)': {
      paddingBottom: '1rem',
      marginBottom: '1rem',
      borderBottom: `0.1rem solid ${colors.neutral[300]}`,
    },
  },
  summaryKey: {
    fontWeight: 700,
    fontSize: '1.6rem',
    lineHeight: '2.4rem',
    color: colors.neutral[900],
    marginBottom: '0.5rem',
  },
  summaryValue: {
    fontSize: '1.6rem',
    lineHeight: '2.4rem',
    color: colors.neutral[600],
  },
  planButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    paddingTop: '2rem',
    borderTop: `0.1rem solid ${colors.neutral[600]}`,
  },
  planDetailsButton: {
    textTransform: 'uppercase',
  },
  rates: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    listStyle: 'none',
    padding: '0 2rem',
  },
  rateWrapper: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1.2fr 1fr',
    columnGap: '1rem',
    '&:not(:last-child)': {
      marginBottom: '1rem',
    },
  },
  rateName: {
    color: colors.neutral[900],
    font: {
      size: '1.4rem',
      lineHeight: '1.8rem',
      weight: 400,
    },
    letterSpacing: '0.02rem',
  },
  rateValue: ({ accentColor }) => ({
    font: {
      size: '1.4rem',
      lineHeight: '1.8rem',
      weight: 700,
    },
    letterSpacing: '0.02rem',
    color: accentColor,
    justifySelf: 'end',
  }),
  carrierWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1rem 0',
  },
  carrierLogo: {
    height: '3rem',
    width: 'auto',
    display: 'block',
  },
};

const createStyles = createUseStyles(styles);
export { createStyles };
